import { useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { Flex } from '@/uicomponents';
import {
  ImpersonationBanner,
  InternalModeBanner,
  NoAccessScreen,
  ScreenLoader,
} from '@/components/shared';
import { useAuthStore } from '@/stores/auth-store';
import { useMeQuery } from '@/app/auth/hooks';
import { endSession, refreshSession } from '@/services/http';
import { setRumUser } from '@/services/datadog';
import { hasRole } from '@/lib/auth/has-role';
import { isServerUnreachableError } from '@/lib/utils';
import { useRumViewTracking } from '@/lib/hooks';
import { AppHeader } from './app-header';
import { ScrollToTop } from './scroll-to-top';
import { ServerUnreachable } from './server-unreachable';
import styles from './app-layout.module.css';

export const AppLayout = () => {
  const { setUser } = useAuthStore();
  const location = useLocation();
  const { data, isLoading, isError, error, refetch, isFetching } = useMeQuery();
  // The user id the startup refresh already ran for. One refresh per session,
  // not one per navigation.
  const refreshedForRef = useRef<string | null>(null);
  useRumViewTracking();

  useEffect(() => {
    if (!data) return;
    setUser(data);
    setRumUser(data);
    if (refreshedForRef.current === data.id) return;
    refreshedForRef.current = data.id;
    // Start the proactive refresh timer for the existing session. Shares the
    // single-flight refresh in http.ts, so it never races an interceptor refresh.
    // If the refresh cookie is dead, endSession logs out on the server so both
    // cookies go. Then /login sees no session and stays put instead of looping.
    refreshSession().catch(endSession);
  }, [data, setUser]);

  if (isLoading) {
    return <ScreenLoader />;
  }

  // Only when there is no user at all. A failed background refetch that still
  // has data from an earlier success must not log the user out.
  if (isError && !data) {
    // No answer from the server is not "signed out". Bouncing to /login would show a
    // signed-in user a login form; stay on the deep link and offer a retry (NPJB-815).
    if (isServerUnreachableError(error)) {
      return <ServerUnreachable onRetry={() => refetch()} retrying={isFetching} />;
    }
    const to = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?to=${encodeURIComponent(to)}`} replace />;
  }

  // NPJB-350: post-cutover every browser user MUST hold either `platform_user`
  // or `admin`. A logged-in user that holds neither is rejected here rather than
  // letting them reach feature surfaces that 403 piecemeal.
  const allowed = hasRole(data, 'platform_user') || hasRole(data, 'admin');
  if (!allowed) {
    return <NoAccessScreen />;
  }

  return (
    <>
      <ScrollToTop />
      <ImpersonationBanner />
      <AppHeader />
      <InternalModeBanner />
      <Flex vertical className={styles.content}>
        <Outlet />
      </Flex>
    </>
  );
};
